import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getPaymentTermBySupplierData } from 'utils/apiRequests';
import {
	chartheight,
	FONTSTYLE,
} from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { colors } from '@alixpartners/ui-components';
import { renderToString } from 'react-dom/server';
import {
	TooltipColor,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemTitle,
	TooltipItemValue,
} from './DashboardChart.styles';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { getRequiredCustomFilterObjectFormat } from '../../CategoryManagement/utils';

const PaymentTermBySupplier = createContext();

export const PaymentTermBySupplierProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [options, setOptions] = useState({
		chart: {
			style: { fontFamily: 'Roboto' },
			height: chartheight,
			zoomType: 'xy',
		},
		title: { text: '' },
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		loading: {
			hideDuration: 500,
			showDuration: 500,
			labelStyle: {
				fontWeight: 'normal',
				letterSpacing: '0.3px',
			},
		},
		xAxis: {
			title: {
				text: ``,
				style: { ...FONTSTYLE },
			},
			min: 0,
			labels: {
				formatter: function() {
					if (typeof this.value !== 'number') {
						if (this.value === undefined) {
							return '';
						} else {
							if (this.value.length > 0) {
								return `${this.value.substring(0, 4)}...`;
							}
						}
					}
				},
				style: {
					...FONTSTYLE,
				},
			},
			lineColor: colors.black,
			gridLineWidth: 0,
		},
		yAxis: [
			{
				gridLineWidth: 0,
				lineColor: colors.black,
				lineWidth: 1,
				title: {
					text: 'Payment Terms',
					style: {
						...FONTSTYLE,
						textOverflow: 'ellipsis',
					},
				},
			},
			{
				gridLineWidth: 0,
				lineColor: colors.black,
				lineWidth: 1,
				labels: {
					formatter: function() {
						return formatSpendWithDecimalAndSymbol(
							this.value,
							currencySymbol,
							hideDecimals,
							0
						).toUpperCase();
					},
					style: {
						...FONTSTYLE,
					},
				},
				title: {
					text: 'Spend',
					style: {
						...FONTSTYLE,
						fontSize: '9px',
						textOverflow: 'ellipsis',
					},
				},
				opposite: true,
			},
		],
		legend: {
			enabled: true,
		},
		tooltip: {
			enabled: true,
			shared: true,
			distance: 56,
			useHTML: true,
			backgroundColor: 'rgba(255,255,255, 0.9)',
			borderWidth: 0,
			outside: true,
			borderRadius: 2,
			formatter: function() {
				return renderToString(
					<TooltipContainer>
						<TooltipItemTitle>{this.x}</TooltipItemTitle>
						{this.points.map(item => (
							<TooltipItem key={`${item.point.series.name}-${item.point.y}`}>
								<span>
									<TooltipColor color={item.color} />
									<TooltipItemName>{item.point.series.name}</TooltipItemName>
								</span>
								<TooltipItemValue>
									{item.colorIndex !== 0
										? formatSpendWithDecimalAndSymbol(
												item.point.y,
												currencySymbol,
												hideDecimals,
												2
										  ).toUpperCase()
										: item.point.y}
								</TooltipItemValue>
							</TooltipItem>
						))}
					</TooltipContainer>
				);
			},
		},
		plotOptions: {
			column: {
				pointPadding: 0,
				groupPadding: 0.1,
			},
			series: {
				borderWidth: 0,
				stacking: 'normal',
				dataLabels: { enabled: false },
				states: { hover: { enabled: false } },
				events: {
					legendItemClick: function() {
						const clickedSeries = this;
						const chart = this.chart;

						// Count how many series are currently visible
						const visibleSeriesCount = chart.series.filter(s => s.visible)
							.length;

						// If all series are visible and we click one, hide all others
						if (visibleSeriesCount === chart.series.length) {
							chart.series.forEach(series => {
								if (series !== clickedSeries) {
									series.hide();
								}
							});
							return false;
						}

						// If only one series is visible (the clicked one), show all series
						if (clickedSeries.visible && visibleSeriesCount === 1) {
							chart.series.forEach(series => {
								series.show();
							});
							return false;
						}

						// If the clicked series is hidden, show only it
						if (!clickedSeries.visible) {
							chart.series.forEach(series => {
								if (series === clickedSeries) {
									series.show();
								} else {
									series.hide();
								}
							});
							return false;
						}

						// If clicked series is visible but not the only visible one, show only it
						chart.series.forEach(series => {
							if (series === clickedSeries) {
								series.show();
							} else {
								series.hide();
							}
						});

						return false; // prevent default toggle behavior
					},
				},
			},
		},
		colors: ['#448EB4', '#032E45'],
	});

	const {
		currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
		customFilters,
		customFiltersValues,
	} = useContext(CategoryManagementContext);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
						taxonomyType: taxonomyType,
					};
					if (
						Array.isArray(customFiltersValues) &&
						customFiltersValues.length > 0
					) {
						payload.customfilter = getRequiredCustomFilterObjectFormat(
							customFilters,
							customFiltersValues
						);
					}
					getPaymentTermBySupplierData(payload)
						.then(response => {
							if (response.succeeded) {
								const allZero = response.data.every(
									item => item.totalSpend === 0 && item.totalSuppliers === 0
								);
								if (response.data !== null && response.data.length > 0) {
									if (!allZero) {
										const sortedData = response.data.sort(
											(a, b) => b.spend - a.spend
										);
										const categoriesResult = [
											...sortedData.map(obj => obj.vendoR_NAME),
										];
										const dpo = sortedData.map(obj => {
											return {
												x: categoriesResult.indexOf(obj.vendoR_NAME),
												y: obj.dpo,
											};
										});
										const spend = sortedData.map(obj => ({
											x: categoriesResult.indexOf(obj.vendoR_NAME),
											y: obj.spend,
										}));
										setOptions(prevState => ({
											...prevState,
											xAxis: {
												...prevState.xAxis,
												categories: categoriesResult,
												tickInterval: 1,
											},

											series: [
												{
													name: 'Payment Terms',
													type: 'line',
													yAxis: 0,
													zIndex: 2,
													data: dpo,
												},
												{
													name: 'Spend',
													type: 'column',
													yAxis: 1,
													data: spend,
												},
											],
										}));
										setData([
											{
												name: 'Payment Terms',
												type: 'line',
												yAxis: 0,
												data: dpo,
											},
											{
												name: 'Spend',
												type: 'column',
												yAxis: 1,
												data: spend,
											},
										]);
									} else {
										setOptions(prevState => ({
											...prevState,
											xAxis: {
												...prevState.xAxis,
												categories: [],
												tickInterval: 1,
											},
											series: [],
										}));
									}
								} else {
									setOptions(prevState => ({
										...prevState,
										xAxis: {
											...prevState.xAxis,
											categories: [],
											tickInterval: 1,
										},
										series: [],
									}));
									setIsLoading(false);
								}
							}
						})
						.catch(() => setIsLoading(false))
						.finally(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			dateRangeData,
			categoryName,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			taxonomyType,
			customFilters,
			customFiltersValues,
		]
	);

	return (
		<PaymentTermBySupplier.Provider value={{ isLoading, data, options }}>
			{children}
		</PaymentTermBySupplier.Provider>
	);
};

export const usePaymentTermBySupplierContext = () => {
	return useContext(PaymentTermBySupplier);
};
